/**
 * 浏览器端读取媒体文件时长（秒）
 *
 * 给参考音频 / 参考视频上传组件复用，避免两边各写一份。
 *
 * 实现方式：
 *   - 把 File 包成 ObjectURL；
 *   - 用临时 <audio> / <video> 元素 preload="metadata"；
 *   - 监听 loadedmetadata 读 duration；
 *   - 失败统一抛错，由调用方决定 UI 表现。
 */

type MediaKind = 'audio' | 'video';

/** 读取媒体文件的时长（秒）。失败时抛出 Error。 */
export function readMediaDuration(file: File, kind: MediaKind): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(kind);
    el.preload = 'metadata';

    const cleanup = () => {
      URL.revokeObjectURL(url);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('error', onError);
    };

    const onLoaded = () => {
      const duration = el.duration;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error('无法读取媒体时长'));
        return;
      }
      resolve(duration);
    };

    const onError = () => {
      cleanup();
      reject(new Error(kind === 'audio' ? '音频解码失败，请换一个文件再试' : '视频解码失败，请换一个文件再试'));
    };

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('error', onError);
    el.src = url;
  });
}

/** 时长格式化，最多保留 1 位小数 */
export function formatSeconds(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
